import { formatCoachingScore } from "@repo/app-types/entities/coachingScore";
import type {
  ComputedFieldDefinition,
  ComputedFieldDefinitionId,
} from "@repo/app-types/entities/computedField";
import type { OutcomeDefinition } from "@repo/app-types/entities/outcome";
import type { PlaybookSectionNode } from "@repo/app-types/entities/playbook";
import { buildTeammateDisplayNames } from "@repo/app-types/entities/teammate";
import {
  getDefaultDirectionForTeammateSortField,
  type GradingsByTeammateSort,
  type GradingsByTeammateSortField,
  type ResolvedTeamPerformancePageRow,
} from "@repo/app-types/entities/teammatePerformanceSummary";
import { getCoachingGoalProgressPill } from "@repo/app-types/helpers/coachingGoalProgress";
import { CoachingGoalPill } from "@repo/ui/coachingNote/coachingGoalPill";
import { SmallTimeStamp } from "@repo/ui/SmallTimeStamp/smallTimeStamp";
import {
  EntityTable,
  type EntityTableColumnDefinition,
} from "@repo/ui/tables/entityTable";
import { TooltipHoverTarget } from "@repo/ui/tooltip/hoverTarget";
import { SimpleTooltip } from "@repo/ui/tooltip/simpleTooltip";
import { TooltipTrigger } from "@repo/ui/tooltip/tooltipTrigger";
import { DateTime } from "luxon";
import { type FC, useCallback, useMemo } from "react";
import type { Key, SortDescriptor } from "react-aria-components";
import {
  buildPerformanceColumns,
  headerWithTooltip,
  PerformanceColumnId,
  performanceColumnIdToSortField,
  performanceSortFieldToColumnId,
  sortPerformanceColumns,
  togglablePerformanceMetricColumnIdSet,
} from "./performanceMetricColumns.js";
import { TeammateCallCount } from "./teammateCallCount.js";

export interface TeammatesTableProps {
  rows: readonly ResolvedTeamPerformancePageRow[];
  medianPassRate: number | undefined;
  medianTalkRatio: number | undefined;
  /** Median outcome success rate across all teammates. Only provided if playbook has an outcome definition. */
  medianOutcomeGreenRate: number | undefined;
  /** Median outcome win count across all teammates (gap-bar benchmark). Only provided if playbook has an outcome definition. */
  medianOutcomeGreenCount: number | undefined;
  /** Team-average of per-teammate "avg revenue per win" (the gap-bar benchmark). Revenue playbooks only. */
  averageRevenueBenchmark: number | undefined;
  /** Median premium-per-hour across all teammates (gap-bar benchmark). Revenue playbooks only. */
  medianRevenuePerHour: number | undefined;
  /** Median recorded call seconds per green outcome (call time on non-N/A-final accounts ÷ green-final accounts) across all teammates (gap-bar benchmark for the "Avg time to <noun>" column). Non-revenue outcome playbooks only. */
  medianSecondsPerGreenOutcome: number | undefined;
  /** Median per-teammate customer-experience score across the cohort (raw 1-5 scale, gap-bar benchmark). */
  medianCustomerExperienceScore: number | undefined;
  /** The outcome definition for the playbook. If provided, the outcome column will be displayed. */
  outcomeDefinition: OutcomeDefinition | undefined;
  shouldShowKnowledgeBaseAdherenceColumn: boolean;
  /** Whether to render the customer-experience score column (gated behind Topics & Signals at the page level). */
  shouldShowCustomerExperienceScoreColumn: boolean;
  /** Whether the top-level Playbook score column should be visible */
  showsPlaybookColumn?: boolean;
  /** Sections to display as separate columns in the table */
  selectedSections?: readonly PlaybookSectionNode[];
  /** Median section scores keyed by section ID */
  medianSectionScores?: Record<string, number>;
  /** Computed field definitions to render as dynamic toggleable columns. */
  computedFields?: readonly ComputedFieldDefinition[];
  /** Median values per computed field id, for gap-bar baselines. */
  medianComputedFieldValues?: Record<ComputedFieldDefinitionId, number | null>;
  sort: GradingsByTeammateSort | undefined;
  onSortChange: (sort: GradingsByTeammateSort | undefined) => void;
  isLoading?: boolean;
  isError?: boolean;
  onTeammateAction: (row: ResolvedTeamPerformancePageRow) => void;
  onLoadMore?: () => void;
  isFullBleed?: boolean;
  /**
   * Set of toggleable performance metric column IDs the user has chosen
   * to display. When omitted, every toggleable column is visible.
   */
  selectedMetricColumnIds?: ReadonlySet<string>;
}

const TeammatesColumnId = {
  Name: "name",
  Calls: PerformanceColumnId.Calls,
  CoachingGoal: PerformanceColumnId.CoachingGoal,
} as const;

const buildColumns = (
  medianPassRate: number | undefined,
  medianTalkRatio: number | undefined,
  medianOutcomeGreenRate: number | undefined,
  medianOutcomeGreenCount: number | undefined,
  averageRevenueBenchmark: number | undefined,
  medianRevenuePerHour: number | undefined,
  medianSecondsPerGreenOutcome: number | undefined,
  medianCustomerExperienceScore: number | undefined,
  outcomeDefinition: OutcomeDefinition | undefined,
  shouldShowKnowledgeBaseAdherenceColumn: boolean,
  shouldShowCustomerExperienceScoreColumn: boolean,
  showsPlaybookColumn: boolean,
  selectedSections: readonly PlaybookSectionNode[],
  medianSectionScores: Record<string, number> | undefined,
  computedFields: readonly ComputedFieldDefinition[],
  medianComputedFieldValues:
    | Record<ComputedFieldDefinitionId, number | null>
    | undefined,
  selectedMetricColumnIds: ReadonlySet<string>,
): readonly EntityTableColumnDefinition<ResolvedTeamPerformancePageRow>[] => {
  const allowsSorting = true;

  const columns: EntityTableColumnDefinition<ResolvedTeamPerformancePageRow>[] =
    [
      {
        id: TeammatesColumnId.Name,
        pinnedToLeft: true,
        isRowHeader: true,
        header: "Teammate",
        renderCell: (row) => {
          const { primaryDisplayName, secondaryDisplayName } =
            buildTeammateDisplayNames(row);

          const lastActivityAt = row.last_activity_at;
          const lastActivityDate = lastActivityAt
            ? DateTime.fromISO(lastActivityAt, {
                locale: "en-US",
              }).toLocaleString()
            : undefined;

          return (
            <>
              <div className="flex items-center gap-1">
                <span className="w-[8rem] max-w-fit flex-auto truncate font-medium">
                  {primaryDisplayName}
                </span>

                {lastActivityAt ? (
                  <TooltipTrigger>
                    <TooltipHoverTarget>
                      <SmallTimeStamp timestamp={lastActivityAt} />
                    </TooltipHoverTarget>
                    <SimpleTooltip>
                      Most recent call on {lastActivityDate}
                    </SimpleTooltip>
                  </TooltipTrigger>
                ) : null}
              </div>

              <div className="flex">
                {secondaryDisplayName ? (
                  <span className="w-0 flex-auto truncate text-gray-600">
                    {secondaryDisplayName}
                  </span>
                ) : null}
              </div>
            </>
          );
        },
      },
    ];

  columns.push(
    ...buildPerformanceColumns<ResolvedTeamPerformancePageRow>({
      benchmarks: {
        medianPassRate,
        medianTalkRatio,
        medianOutcomeGreenRate,
        medianOutcomeGreenCount,
        averageRevenueBenchmark,
        medianRevenuePerHour,
        medianSecondsPerGreenOutcome,
        medianSectionScores,
        medianCustomerExperienceScore,
        medianComputedFieldValues,
      },
      outcomeDefinition,
      shouldShowKnowledgeBaseAdherenceColumn,
      shouldShowCustomerExperienceScoreColumn,
      showsPlaybookColumn,
      selectedSections,
      computedFields,
      selectedMetricColumnIds,
    }),
  );

  if (selectedMetricColumnIds.has(TeammatesColumnId.CoachingGoal)) {
    columns.push({
      id: TeammatesColumnId.CoachingGoal,
      // Distribute remaining space to the other columns.
      className: "w-0",
      allowsSorting,
      header: headerWithTooltip(
        "Goal progress",
        "Standing on this week's coaching goal, from the current score versus its target. Smoothed toward the starting score while the week still has calls to come.",
      ),
      renderCell: (row) => (
        <TeammateCoachingGoalCell coachingGoal={row.coaching_goal} />
      ),
    });
  }

  if (selectedMetricColumnIds.has(TeammatesColumnId.Calls)) {
    columns.push({
      id: TeammatesColumnId.Calls,
      // Distribute remaining space to the other columns.
      className: "w-0",
      allowsSorting,
      header: headerWithTooltip("Calls", "Number of calls in this period."),
      renderCell: (row) => {
        return <TeammateCallCount row={row} />;
      },
    });
  }

  return sortPerformanceColumns(columns);
};

const TeammateCoachingGoalCell: FC<{
  coachingGoal: ResolvedTeamPerformancePageRow["coaching_goal"];
}> = ({ coachingGoal }) => {
  if (!coachingGoal) {
    return <span className="text-muted-foreground">--</span>;
  }

  const {
    criterion_name: criterionName,
    grading_value_type: gradingValueType,
    started_score: startedScore,
    score_target: scoreTarget,
    current_score: currentScore,
    scored_call_count: scoredCallCount,
    progress_state: progressState,
  } = coachingGoal;
  const pill = getCoachingGoalProgressPill(progressState);
  const formattedTarget = formatCoachingScore(scoreTarget, gradingValueType);

  return (
    <TooltipTrigger>
      <TooltipHoverTarget shape="rounded">
        <CoachingGoalPill pill={pill} />
      </TooltipHoverTarget>
      <SimpleTooltip>
        {scoredCallCount === 0 ? (
          <>
            {criterionName}: no scored calls yet this week, target{" "}
            {formattedTarget}.
          </>
        ) : (
          <>
            {criterionName}:{" "}
            {formatCoachingScore(
              currentScore ?? startedScore,
              gradingValueType,
            )}{" "}
            now, started at{" "}
            {formatCoachingScore(startedScore, gradingValueType)}, target{" "}
            {formattedTarget}.
          </>
        )}
      </SimpleTooltip>
    </TooltipTrigger>
  );
};

function mapColumnIdToSortField(
  columnId: Key | undefined,
): GradingsByTeammateSortField | undefined {
  switch (columnId) {
    case TeammatesColumnId.Calls:
      return "recording_count";

    case TeammatesColumnId.CoachingGoal:
      return "coaching_goal_progress";
  }

  return performanceColumnIdToSortField(columnId);
}

function mapSortFieldToColumnId(
  sortField: GradingsByTeammateSortField | undefined,
): string | undefined {
  switch (sortField) {
    case "recording_count":
      return TeammatesColumnId.Calls;

    case "last_recorded_at":
      return undefined;

    case "coaching_goal_progress":
      return TeammatesColumnId.CoachingGoal;
  }

  return performanceSortFieldToColumnId(sortField);
}

export const TeammatesTable: FC<TeammatesTableProps> = (props) => {
  const selectedMetricColumnIds =
    props.selectedMetricColumnIds ?? togglablePerformanceMetricColumnIdSet;
  const columns = useMemo(
    () =>
      buildColumns(
        props.medianPassRate,
        props.medianTalkRatio,
        props.medianOutcomeGreenRate,
        props.medianOutcomeGreenCount,
        props.averageRevenueBenchmark,
        props.medianRevenuePerHour,
        props.medianSecondsPerGreenOutcome,
        props.medianCustomerExperienceScore,
        props.outcomeDefinition,
        props.shouldShowKnowledgeBaseAdherenceColumn,
        props.shouldShowCustomerExperienceScoreColumn,
        props.showsPlaybookColumn ?? true,
        props.selectedSections ?? [],
        props.medianSectionScores,
        props.computedFields ?? [],
        props.medianComputedFieldValues,
        selectedMetricColumnIds,
      ),
    [
      props.medianPassRate,
      props.medianTalkRatio,
      props.medianOutcomeGreenRate,
      props.medianOutcomeGreenCount,
      props.averageRevenueBenchmark,
      props.medianRevenuePerHour,
      props.medianSecondsPerGreenOutcome,
      props.medianCustomerExperienceScore,
      props.outcomeDefinition,
      props.shouldShowKnowledgeBaseAdherenceColumn,
      props.shouldShowCustomerExperienceScoreColumn,
      props.showsPlaybookColumn,
      props.selectedSections,
      props.medianSectionScores,
      props.computedFields,
      props.medianComputedFieldValues,
      selectedMetricColumnIds,
    ],
  );

  const sortField = props.sort?.field;
  const sortDirection = props.sort?.direction;
  const sortDescriptor = useMemo((): SortDescriptor | undefined => {
    const column = mapSortFieldToColumnId(sortField);
    if (!column) {
      return undefined;
    }

    return {
      column,
      direction: sortDirection === "asc" ? "ascending" : "descending",
    };
  }, [sortField, sortDirection]);

  const onSortChange = props.onSortChange;
  const handleSortChange = useCallback(
    (descriptor: SortDescriptor | undefined) => {
      const field = mapColumnIdToSortField(descriptor?.column);
      if (!field) {
        onSortChange(undefined);
        return;
      }

      const requestedDirection =
        descriptor?.direction === "ascending" ? "asc" : "desc";
      const didFieldChange = field !== sortField;

      onSortChange({
        field,
        // If we picked a new field, default to the field's default direction.
        direction: didFieldChange
          ? getDefaultDirectionForTeammateSortField(field)
          : requestedDirection,
      });
    },
    [sortField, onSortChange],
  );

  return (
    <EntityTable
      aria-label="Teammates"
      columns={columns}
      entities={props.rows}
      isLoading={props.isLoading}
      isError={props.isError}
      onLoadMore={props.onLoadMore}
      emptyMessage="No teammates with any activity yet."
      onEntityAction={props.onTeammateAction}
      isFullBleed={props.isFullBleed}
      sortDescriptor={sortDescriptor}
      onSortChange={handleSortChange}
    />
  );
};
